/**
 * FloatingActionBar.js
 *
 * Responsive floating action bar for voyage actions.
 *
 * PHASE 3 v2 - REDESIGNED MOBILE LAYOUT:
 * - Mobile (< 640px): 4 buttons (Back | Log | Operation | Menu)
 *   - Back: Returns to Operations standard view with tabs
 *   - Log: Opens LogPickerModal with Position/Weather/Fuel/Note/Compass/Arrive
 *   - Operation: Start or Complete active operation
 *   - Menu: Opens main app navigation
 *
 * - Tablet (640-1024px): 5 buttons + More
 * - Desktop (> 1024px): All 7 buttons
 */

import React, { useState } from 'react';
import {
  MapPin,
  Cloud,
  FileText,
  Play,
  Anchor,
  Fuel,
  Compass,
  MoreHorizontal,
  X,
  Menu,
  ArrowLeft,
  ClipboardList
} from 'lucide-react';
import LogPickerModal from './LogPickerModal';

// Action button component - reused across breakpoints
const ActionButton = ({ icon: Icon, label, onClick, color = 'slate', disabled = false, highlight = false, size = 'default' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`
      flex flex-col items-center gap-1 rounded-lg transition-all
      ${size === 'large' ? 'p-3 min-w-[64px]' : 'p-2 sm:p-3 min-w-[56px]'}
      disabled:opacity-40 disabled:cursor-not-allowed
      ${highlight
        ? 'bg-green-600 hover:bg-green-700 text-white'
        : `text-${color}-600 hover:bg-${color}-50 active:bg-${color}-100`
      }
    `}
  >
    <Icon className={size === 'large' ? 'h-6 w-6' : 'h-5 w-5 sm:h-6 sm:w-6'} />
    <span className={size === 'large' ? 'text-xs font-medium' : 'text-[10px] sm:text-xs font-medium'}>{label}</span>
  </button>
);

// Menu item for expanded "More" menu (tablet)
const MenuItem = ({ icon: Icon, label, onClick, color = 'slate', disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`
      flex items-center gap-3 w-full px-4 py-3 text-left transition-colors
      disabled:opacity-40 disabled:cursor-not-allowed
      hover:bg-slate-50 active:bg-slate-100
    `}
  >
    <Icon className={`h-5 w-5 text-${color}-600`} />
    <span className="font-medium text-slate-700">{label}</span>
  </button>
);

const FloatingActionBar = ({
  onLogPosition,
  onLogWeather,
  onLogNote,
  onLogFuel,
  onCompassCheck,
  onStartOperation,
  // FT-283: onCompleteOperation prop deleted. Complete is now owned by the
  // <CompleteOperationRow> at the bottom of the active operation's checklist
  // inside VoyageDocument.
  onPrepareForArrival,
  onDailyChecks,      // FT-80: Opens Daily Checks quick-access
  dailyChecksEnabled = false,  // FT-80: Only show Daily Checks in Log picker if enabled in Voyage Settings
  hasActiveOperation = false,  // FT-283: still needed to gate actions.arrive.disabled
  activeOperationType,
  preArrivalDone = false,  // FT-285 Item 4: hard-lock Arrive once pre_arrival operation_complete exists
  isReadOnly = false,
  onMenuClick,       // Opens main app navigation
  onBack             // Returns to Operations standard view (tabs visible)
}) => {
  const [showMore, setShowMore] = useState(false);
  const [showLogPicker, setShowLogPicker] = useState(false);

  if (isReadOnly) return null;

  // Define all actions with their properties
  // FT-283: actions.operation collapsed to always Start. Complete now lives in
  // <CompleteOperationRow> at the bottom of the active checklist (VoyageDocument).
  const actions = {
    position: { icon: MapPin, label: 'Position', onClick: onLogPosition, color: 'blue' },
    weather: { icon: Cloud, label: 'Weather', onClick: onLogWeather, color: 'cyan' },
    fuel: { icon: Fuel, label: 'Fuel', onClick: onLogFuel, color: 'amber' },
    compass: { icon: Compass, label: 'Compass', onClick: onCompassCheck, color: 'amber' },
    note: { icon: FileText, label: 'Note', onClick: onLogNote, color: 'slate' },
    // FT-285 Item 4: disable when pre_arrival operation_complete already exists.
    arrive: { icon: Anchor, label: 'Arrive', onClick: onPrepareForArrival, color: 'amber', disabled: hasActiveOperation || preArrivalDone },
    operation: { icon: Play, label: 'Operation', onClick: onStartOperation, color: 'cyan', disabled: hasActiveOperation }
  };

  // Handle menu action - close menu and execute
  const handleMenuAction = (callback) => {
    setShowMore(false);
    callback?.();
  };

  return (
    <>
      {/* Log Picker Modal - mobile only */}
      <LogPickerModal
        isOpen={showLogPicker}
        onClose={() => setShowLogPicker(false)}
        onSelectPosition={onLogPosition}
        onSelectWeather={onLogWeather}
        onSelectFuel={onLogFuel}
        onSelectNote={onLogNote}
        onSelectCompass={onCompassCheck}
        onSelectArrive={onPrepareForArrival}
        onSelectDailyChecks={onDailyChecks}
        hasActiveOperation={hasActiveOperation}
        preArrivalDone={preArrivalDone}  // FT-285 Item 4
        dailyChecksEnabled={dailyChecksEnabled}
      />

      {/* More Menu Overlay - TABLET ONLY */}
      {showMore && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setShowMore(false)}
          />

          {/* Menu Panel */}
          <div className="fixed bottom-20 right-4 left-4 sm:left-auto sm:right-4 sm:w-64 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
            {/* Menu Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50">
              <span className="text-sm font-medium text-slate-600">More Actions</span>
              <button
                onClick={() => setShowMore(false)}
                className="p-1 hover:bg-slate-200 rounded"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            {/* Menu Items - tablet only shows compass & arrive */}
            <div className="py-1">
              <div className="hidden sm:block lg:hidden">
                <MenuItem {...actions.compass} onClick={() => handleMenuAction(actions.compass.onClick)} />
                <MenuItem {...actions.arrive} onClick={() => handleMenuAction(actions.arrive.onClick)} />
              </div>

              {/* On desktop: menu shouldn't be needed */}
              <div className="hidden lg:block">
                <p className="px-4 py-2 text-sm text-slate-400">All actions visible</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-30 print:hidden safe-area-bottom">

        {/* ===== MOBILE: 4 buttons (matching MobileBottomBar sizing) ===== */}
        <div className="sm:hidden flex items-center justify-around px-4 py-3 max-w-md mx-auto">
          {/* Back button */}
          <button
            onClick={onBack}
            className="flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors min-h-[56px] min-w-[56px]"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-xs font-medium">Back</span>
          </button>

          {/* Log button - opens picker */}
          <button
            onClick={() => setShowLogPicker(true)}
            className="flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 transition-colors min-h-[56px] min-w-[56px]"
          >
            <ClipboardList className="h-5 w-5" />
            <span className="text-xs font-medium">Log</span>
          </button>

          {/* Operation button - Start only.
              FT-283: the mobile Complete branch is deleted. Complete now lives
              in the <CompleteOperationRow> at the bottom of the active
              operation's checklist (VoyageDocument). When an operation is
              already active, the Start button is disabled (one op at a time). */}
          <button
            onClick={onStartOperation}
            disabled={hasActiveOperation}
            className="flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors min-h-[56px] min-w-[56px]"
          >
            <Play className="h-5 w-5" />
            <span className="text-xs font-medium">Operation</span>
          </button>

          {/* Menu button */}
          <button
            onClick={onMenuClick}
            className="flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors min-h-[56px] min-w-[56px]"
          >
            <Menu className="h-5 w-5" />
            <span className="text-xs font-medium">Menu</span>
          </button>
        </div>

        {/* ===== TABLET: 5 buttons + More ===== */}
        <div className="hidden sm:flex lg:hidden items-center justify-around p-2 max-w-lg mx-auto">
          <ActionButton {...actions.position} />
          <ActionButton {...actions.weather} />
          <ActionButton {...actions.fuel} />
          <ActionButton {...actions.note} />
          <ActionButton {...actions.operation} />
          <button
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-col items-center gap-1 p-3 rounded-lg transition-all min-w-[56px] ${
              showMore ? 'bg-slate-200 text-slate-700' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <MoreHorizontal className="h-6 w-6" />
            <span className="text-xs font-medium">More</span>
          </button>
        </div>

        {/* ===== DESKTOP: All 7 buttons ===== */}
        <div className="hidden lg:flex items-center justify-around p-2 max-w-2xl mx-auto">
          <ActionButton {...actions.position} />
          <ActionButton {...actions.weather} />
          <ActionButton {...actions.fuel} />
          <ActionButton {...actions.compass} />
          <ActionButton {...actions.note} />
          <ActionButton {...actions.arrive} />
          <ActionButton {...actions.operation} />
        </div>

      </div>

      {/* Safe area styles */}
      <style>{`
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </>
  );
};

export default FloatingActionBar;
